import { useState, forwardRef, FocusEvent, ReactNode, useEffect, useMemo } from 'react'
import { Accept, ErrorCode, useDropzone } from 'react-dropzone'
import { isEmpty, isNil } from 'lodash'
import { useTranslations } from 'next-intl'
import { DEFAULT_ACCEPTED_FILES, MAX_SIZE_PAYLOAD } from '@/shared/enums/constants'
import Paragraph from './Paragraph'
import Button from './Button'
import { FileUp, Info } from 'lucide-react'
import Icon from './Icon'
import ImageFileTypeLogo from './ImageFileTypeLogo'
import Tooltip from './Tooltip'

const mimeTypeToExtension: Record<string, string> = {
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'application/pdf': 'PDF',
    'application/json': 'JSON',
    'text/csv': 'CSV',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.ms-powerpoint': 'PPT',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
    'text/plain': 'TXT'
}

export type UploadFileProps = {
    onChange?: (event: { target: { value: File[] | undefined; name?: string } }) => void
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void
    value?: File[] | null
    label?: string
    accept?: Accept
    message?: string
    required?: boolean
    name?: string
    tooltip?: ReactNode
    hideFileList?: boolean
}

const UploadFile = forwardRef<HTMLInputElement, UploadFileProps>(
    (
        {
            onChange,
            onBlur,
            value,
            tooltip,
            required,
            label,
            message,
            accept = DEFAULT_ACCEPTED_FILES,
            name,
            hideFileList
        },
        ref
    ) => {
        const t = useTranslations('root')
        const [files, setFiles] = useState<File[]>(value || [])
        const [errors, setErrors] = useState<ErrorCode[]>([])

        useEffect(() => {
            setFiles(value || [])
        }, [value])

        const acceptedFileTypes = useMemo(() => {
            if (!accept || Object.keys(accept).length === 0) {
                return { fileTypes: '', maxSizeMB: (MAX_SIZE_PAYLOAD / (1024 * 1024)).toFixed(1) }
            }

            const fileTypes: string[] = []

            Object.keys(accept).forEach((mimeType) => {
                if (mimeTypeToExtension[mimeType]) {
                    if (!fileTypes.includes(mimeTypeToExtension[mimeType])) {
                        fileTypes.push(mimeTypeToExtension[mimeType])
                    }
                } else if (mimeType.includes('*')) {
                    const type = mimeType.split('/')[0]
                    if (type === 'image' && !fileTypes.includes('Images')) {
                        fileTypes.push('Images')
                    } else if (type === 'audio' && !fileTypes.includes('Audio')) {
                        fileTypes.push('Audio')
                    } else if (type === 'video' && !fileTypes.includes('Video')) {
                        fileTypes.push('Video')
                    }
                }
            })

            fileTypes.sort()
            const maxSizeMB = (MAX_SIZE_PAYLOAD / (1024 * 1024)).toFixed(1)

            return {
                fileTypes: fileTypes.join(', '),
                maxSizeMB
            }
        }, [accept])

        const handleFilesChange = (newFiles: File[]) => {
            setFiles(newFiles)
            onChange?.({
                target: {
                    value: newFiles.length > 0 ? newFiles : undefined,
                    name
                }
            })
        }

        const { getRootProps, getInputProps } = useDropzone({
            accept,
            maxSize: MAX_SIZE_PAYLOAD,
            onDrop: (acceptedFiles, fileRejections) => {
                const newFiles = [...files, ...acceptedFiles]
                handleFilesChange(newFiles)
                setErrors(
                    fileRejections.map((rejection) => rejection.errors.map((error) => error.code as ErrorCode)).flat()
                )
            }
        })

        const removeFile = (fileToRemove: File) => {
            const newFiles = files.filter((f) => f !== fileToRemove)
            handleFilesChange(newFiles)
        }

        return (
            <div className="grid grid-cols-1 gap-y-2">
                <div>
                    {!isNil(label) ? (
                        <Paragraph
                            variant="medium"
                            size={3}
                            color={!isEmpty(errors) ? 'negative' : !isEmpty(files) ? 'brand' : undefined}
                            className="flex items-center mb-2"
                        >
                            {label}
                            {required && <span className="text-textNegative ml-1">*</span>}
                            {tooltip && (
                                <Tooltip
                                    content={tooltip}
                                    trigger={
                                        <span>
                                            <Icon icon={Info} size="1" className="ml-1" />
                                        </span>
                                    }
                                />
                            )}
                        </Paragraph>
                    ) : null}
                    <div
                        {...getRootProps()}
                        data-testid="__upload-file-dropzone__"
                        className="bg-backgroundEmphasis border-0.5 border-dashed border-borderPrimary border-primary rounded-md p-5 text-center cursor-pointer"
                    >
                        <input
                            {...getInputProps()}
                            ref={ref}
                            name={name}
                            onBlur={onBlur}
                            data-testid="__upload-file-input__"
                        />
                        <div className="flex flex-col gap-1 items-center justify-center">
                            <div className="flex gap-3">
                                <Icon icon={FileUp} />
                                <Paragraph color="brand">{t('components.uploadFile.title')}</Paragraph>
                            </div>
                            <Paragraph color="darkGrey">
                                {t('components.uploadFile.filePropertyDescription', acceptedFileTypes)}
                            </Paragraph>
                        </div>
                    </div>
                </div>
                {!hideFileList && !isEmpty(files) ? (
                    <div className="grid grid-cols-1 gap-4">
                        {files?.map((file, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between border-0.5 border-borderPrimary rounded-md gap-3 p-3"
                            >
                                <ImageFileTypeLogo file={file} />
                                <Paragraph className="truncate">{file.name}</Paragraph>
                                <Button
                                    data-testid={`__upload-file-trash-${index}__`}
                                    intent="negative"
                                    type="button"
                                    onClick={() => removeFile(file)}
                                >
                                    {t('components.uploadFile.delete')}
                                </Button>
                            </div>
                        ))}
                    </div>
                ) : null}
                {!isEmpty(errors) ? (
                    <div data-testid="__upload-file-errors__">
                        {errors.map((error, index) => (
                            <Paragraph color="negative" key={index}>
                                {t(`errors.files.${error}`)}
                            </Paragraph>
                        ))}
                    </div>
                ) : null}

                {message ? (
                    <Paragraph size={3} color="negative" data-testid="__upload-file-message__">
                        {message}
                    </Paragraph>
                ) : null}
            </div>
        )
    }
)

export default UploadFile
